import React, { useState, useEffect } from 'react';
import { login as loginAPI, register as registerAPI } from './api/auth';
import toastManager from './utils/toastManager';
import heartbeatManager from './utils/heartbeat';
import { useNavigate } from 'react-router-dom';
import { isWechatBrowser } from './config/wechat';

export default function Login({ onLogin }) {
  const [isLogin, setIsLogin] = useState(true);
  const [username, setUsername] = useState('');
  const [password, setPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [loading, setLoading] = useState(false);
  const navigate = useNavigate();

  // 检查是否已有登录状态，如果有则跳转到表单列表
  useEffect(() => {
    const checkLoginStatus = () => {
      console.log('🔍 Login: 检查登录状态');
      
      // 检查是否有token
      const token = localStorage.getItem('token');
      if (token) {
        console.log('✅ Login: 检测到已登录状态，跳转到表单列表');
        navigate('/forms', { replace: true });
        return;
      }
      
      // 检查是否是手动退出登录，如果是则清除标识
      const isManualLogout = localStorage.getItem('manualLogout') === 'true';
      if (isManualLogout) {
        console.log('🚪 Login: 检测到手动退出登录，清除标识');
        localStorage.removeItem('manualLogout');
      }
    };

    // 延迟执行检查，确保组件完全加载
    setTimeout(checkLoginStatus, 100);
  }, [navigate]);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);

    try {
      if (isLogin) {
        // 登录逻辑
        const response = await loginAPI({ username, password });
        
        // 检查响应格式
        if (response.code === 200 && response.data && response.data.token) {
          const token = response.data.token;
          localStorage.setItem('token', token);
          localStorage.setItem('isLoggedIn', '1');
          
          // 保存用户信息
          if (response.data.avatar) {
            localStorage.setItem('userAvatar', response.data.avatar);
          }
          if (response.data.accountNickName) {
            localStorage.setItem('userNickName', response.data.accountNickName);
          }
          
          // 保存权限信息
          if (response.data.hasEditAllPermission !== undefined) {
            localStorage.setItem('hasEditAllPermission', response.data.hasEditAllPermission.toString());
          }
        } else {
          toastManager.error('登录失败，未获取到token');
          setLoading(false);
          return;
        }
        
        // 登录成功后启动心跳机制
        console.log('登录成功，启动心跳机制');
        heartbeatManager.start();
        
        if (onLogin) onLogin();
        navigate('/forms'); // 登录成功后跳转到表单列表
      } else {
        // 注册逻辑
        if (!username || !password) {
          toastManager.error('请填写完整信息');
          setLoading(false);
          return;
        }
        if (password !== confirmPassword) {
          toastManager.error('两次输入的密码不一致');
          setLoading(false);
          return;
        }
        if (password.length < 6) {
          toastManager.error('密码长度至少6位');
          setLoading(false);
          return;
        }
        await registerAPI({ username, password });
        // 注册成功，不显示提示
        setIsLogin(true);
        setUsername('');
        setPassword('');
        setConfirmPassword('');
      }
    } catch (error) {
      // 错误消息已通过API层处理，这里不需要额外处理
    } finally {
      setLoading(false);
    }
  };

  const switchMode = () => {
    setIsLogin(!isLogin);
    setUsername('');
    setPassword('');
    setConfirmPassword('');
  };

  return (
    <div style={{ minHeight: '100vh', display: 'flex', alignItems: 'center', justifyContent: 'center', background: '#f5f7fa' }}>
      <form onSubmit={handleSubmit} style={{ background: '#fff', padding: 40, borderRadius: 12, boxShadow: '0 4px 24px rgba(0,0,0,0.08)', minWidth: 320 }}>
        <h2 style={{ textAlign: 'center', marginBottom: 32, color: '#333' }}>
          {isLogin ? '登录' : '注册'}
        </h2>
        
        <div style={{ marginBottom: 20 }}>
          <label style={{ display: 'block', marginBottom: 8, color: '#666' }}>账号</label>
          <input
            type="text"
            value={username}
            onChange={e => setUsername(e.target.value)}
            style={{ width: '100%', padding: 10, borderRadius: 4, border: '1px solid #e0e0e0', fontSize: 16 }}
            placeholder="请输入账号"
            autoFocus
            disabled={loading}
          />
        </div>
        
        <div style={{ marginBottom: 20 }}>
          <label style={{ display: 'block', marginBottom: 8, color: '#666' }}>密码</label>
          <input
            type="password"
            value={password}
            onChange={e => setPassword(e.target.value)}
            style={{ width: '100%', padding: 10, borderRadius: 4, border: '1px solid #e0e0e0', fontSize: 16 }}
            placeholder={isLogin ? "请输入密码" : "请输入密码（至少6位）"}
            disabled={loading}
          />
        </div>
        
        {!isLogin && (
          <div style={{ marginBottom: 20 }}>
            <label style={{ display: 'block', marginBottom: 8, color: '#666' }}>确认密码</label>
            <input
              type="password"
              value={confirmPassword}
              onChange={e => setConfirmPassword(e.target.value)}
              style={{ width: '100%', padding: 10, borderRadius: 4, border: '1px solid #e0e0e0', fontSize: 16 }}
              placeholder="请再次输入密码"
              disabled={loading}
            />
          </div>
        )}
        
        <button 
          type="submit" 
          disabled={loading}
          style={{ 
            width: '100%', 
            padding: 12, 
            background: loading ? '#ccc' : '#1890ff', 
            color: '#fff', 
            border: 'none', 
            borderRadius: 6, 
            fontSize: 16, 
            cursor: loading ? 'not-allowed' : 'pointer',
            marginBottom: 16
          }}
        >
          {loading ? '处理中...' : (isLogin ? '登录' : '注册')}
        </button>
        
        <div style={{ textAlign: 'center' }}>
          <button
            type="button"
            onClick={switchMode}
            disabled={loading}
            style={{
              background: 'none',
              border: 'none',
              color: loading ? '#ccc' : '#1890ff',
              cursor: loading ? 'not-allowed' : 'pointer',
              fontSize: 14,
              textDecoration: 'underline'
            }}
          >
            {isLogin ? '没有账号？立即注册' : '已有账号？立即登录'}
          </button>
        </div>
      </form>
    </div>
  );
} 